import { apiCall } from '@/lib/api-client';

/**
 * Shape returned by GET /agents/me/clients/search?q=&limit=.
 * Re-exported from bookings.api so callers can stop reaching into the
 * bookings feature for a generic primitive.
 */
export interface PatientSearchResult {
  id: string;
  cinHash: string;
  cinLast6: string;
  firstName: string;
  lastName: string;
  dob: string;
  phone: string | null;
}

/**
 * Aggregate returned by GET /clients/:id. Mirrors `ClientProfile` in
 * api/src/clients/clients.service.ts. PHI fields are decrypted server-side
 * inside the EncryptionService transaction.
 */
export interface ClientProfile {
  id: string;
  cinHash: string;
  cinLast6: string;
  firstName: string;
  lastName: string;
  dob: string;
  phoneCell: string | null;
  phoneHome: string | null;
  phoneOther: string | null;
  addressLine1: string | null;
  city: string | null;
  postalCode: string | null;
  isBronxZip: boolean;
  preferredLanguage: string | null;
  screeningStatus: string;
  doNotCall: boolean;
  dncReason: string | null;
  dncSetAt: string | null;
  lastScreenedAt: string | null;
  attemptCount: number;
  lastAttemptAt: string | null;
  activeAssignment: {
    id: string;
    agentId: string;
    agentName: string | null;
    status: string;
    priority: number;
    assignedAt: string;
  } | null;
  bookings: Array<{
    id: string;
    scheduledAt: string;
    status: string;
    agentId: string;
    agentName: string | null;
    notes: string | null;
  }>;
  recentScreenings: Array<{
    id: string;
    agentId: string | null;
    agentName: string | null;
    startedAt: string;
    completedAt: string | null;
    submittedAt: string | null;
    isSubmitted: boolean;
  }>;
  // Cluster H provenance fields — exposed by clients.service.getProfile when
  // the client was created via household_promotion. The frontend
  // ClientProfilePage banner renders only when promotedFrom != null.
  createdVia: string;
  promotedFrom: {
    householdMemberId: string;
    parentScreeningId: string | null;
    parentClientName: string | null;
    promotedAt: string | null;
    promotedByEmail: string | null;
    promotedByName: string | null;
  } | null;
  // Cluster H v2 Fase 2.7 — bidirectional referral chain. Distinct from
  // promotedFrom (used by the existing PromotedBanner): referredBy carries
  // the clickable parentClientId + cinLast6 needed by ReferralChainCard.
  referredBy: {
    householdMemberId: string;
    parentClientId: string;
    parentClientLastName: string;
    parentClientCinLast6: string;
    promotedAt: string;
    promotedByAgentName: string | null;
  } | null;
  linkedHouseholdPromotions: Array<{
    clientId: string;
    clientLastName: string;
    clientCinLast6: string;
    householdMemberId: string;
    promotedAt: string;
    promotedByAgentName: string | null;
  }>;
}

export const clientsApi = {
  /**
   * Typeahead patient search. Returns ALL non-deleted clients regardless of
   * the requester's role (uploader is excluded at the controller @Roles
   * decorator). Used by the global PatientSearchBar.
   *
   * Backend: GET /agents/me/clients/search?q=&limit=
   */
  searchPatients: (q: string, limit: number = 20, signal?: AbortSignal) =>
    apiCall<PatientSearchResult[]>(
      `/agents/me/clients/search?q=${encodeURIComponent(q)}&limit=${limit}`,
      { signal },
    ),

  /** Read-only patient profile. Audit-logged as VIEW_CLIENT on the backend. */
  getProfile: (clientId: string) =>
    apiCall<ClientProfile>(`/clients/${encodeURIComponent(clientId)}`),

  /**
   * Agent (or supervisor) requests admin intervention on this client.
   * Used from the profile when the client is assigned to another agent or
   * has no assignment yet. Backend creates one audit entry + a notif per
   * active admin (Cluster A followup — A-1).
   */
  requestReassign: (clientId: string, reason?: string) =>
    apiCall<void>(`/clients/${encodeURIComponent(clientId)}/request-reassign`, {
      method: 'POST',
      body: reason ? { reason } : {},
    }),

  /**
   * Admin-only: reset the client's call attempt counter. Reason is required
   * (min 3 chars). Audit-logged with previous count + notif to last agent
   * who attempted the client (Cluster A followup — A-3).
   */
  resetAttempts: (clientId: string, reason: string) =>
    apiCall<void>(`/clients/${encodeURIComponent(clientId)}/reset-attempts`, {
      method: 'POST',
      body: { reason },
    }),

  /**
   * v0.6.1 Fase 9 — Cluster ED Phase 1: agent/admin edit client address +
   * phones. Reason is required (min 5 chars). Recomputes is_bronx_zip
   * server-side when postalCode changes; returned ClientProfile reflects
   * the updated eligibility.
   */
  editInfo: (
    clientId: string,
    body: {
      address1?: string;
      address2?: string;
      city?: string;
      postalCode?: string;
      phoneHome?: string;
      phoneCell?: string;
      reason: string;
    },
  ) =>
    apiCall<ClientProfile>(`/clients/${encodeURIComponent(clientId)}/info`, {
      method: 'PATCH',
      body,
    }),
};
